import { useLayoutEffect } from 'react';
import { usePrevious } from 'react-use';

import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { visitPaths } from '~libs.desktop.shared.paths';

import {
   FILTER_SEPARATOR,
   mapPractitionerIdsFilterToPractitionerIds,
   PRACTITIONER_FILTER_STORAGE_KEY,
} from './DoctorsFilter.utils';

type DoctorsFilterSessionStorageStateOutput = {
   getStoredPractitionerIds: () => PractitionerId[];
   setPractitionerIdsFilter: (practitionerIds: PractitionerId[]) => void;
   removePractitionerIdsFilter: VoidFunction;
};

export const doctorsFilterSessionStorage = ((): DoctorsFilterSessionStorageStateOutput => {
   const removePractitionerIdsFilter = (): void => sessionStorage.removeItem(PRACTITIONER_FILTER_STORAGE_KEY);
   const setPractitionerIdsFilter = (practitionerIds: PractitionerId[]): void => {
      if (!practitionerIds?.length) {
         removePractitionerIdsFilter();
         return;
      }
      sessionStorage.setItem(PRACTITIONER_FILTER_STORAGE_KEY, practitionerIds.map((id) => id.asNumber()).join(','));
   };

   return {
      getStoredPractitionerIds: () =>
         mapPractitionerIdsFilterToPractitionerIds(sessionStorage.getItem(PRACTITIONER_FILTER_STORAGE_KEY)),
      setPractitionerIdsFilter: setPractitionerIdsFilter,
      removePractitionerIdsFilter: removePractitionerIdsFilter,
   };
})();

export const useSyncPractitionerIdsFilterParamWithStorage = (
   searchParams: URLSearchParams,
   onSync: (searchParams: URLSearchParams) => void
): void => {
   const practitionerIdsFilterParam = searchParams.get(visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM);
   const previousPractitionerIdsFilterParam = usePrevious(practitionerIdsFilterParam);
   const memoizedSelectedPractitionerIds = doctorsFilterSessionStorage.getStoredPractitionerIds();

   useLayoutEffect(() => {
      if (practitionerIdsFilterParam && practitionerIdsFilterParam !== previousPractitionerIdsFilterParam) {
         const practitionerIdsNumber = practitionerIdsFilterParam.split(FILTER_SEPARATOR).map(Number);
         const practitionerIdsValid = practitionerIdsNumber.every((id) => !isNaN(id));

         if (!practitionerIdsValid) {
            searchParams.delete(visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM);
            onSync(searchParams);
         }

         if (practitionerIdsValid) {
            const practitionerIds = practitionerIdsNumber.map((id) => PractitionerId.of(id));
            doctorsFilterSessionStorage.setPractitionerIdsFilter(practitionerIds);
         }
      }

      if (memoizedSelectedPractitionerIds?.length) {
         const practitionerIdsFromSearchParam = practitionerIdsFilterParam?.split(FILTER_SEPARATOR).map(Number);
         const memoizedAsSet = new Set(memoizedSelectedPractitionerIds.map((id) => id.asNumber()));

         if (!practitionerIdsFromSearchParam || practitionerIdsFromSearchParam.some((id) => !memoizedAsSet.has(id))) {
            const newParamValue = Array.from(memoizedAsSet).join(FILTER_SEPARATOR);
            searchParams.set(visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM, newParamValue);
            onSync(searchParams);
         }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [practitionerIdsFilterParam, memoizedSelectedPractitionerIds]);
};
